/**
 * src/shell/ui/screens/ComputerScreen.tsx — S2 电脑 · 公司终端（设计稿 §1.2 S2）。
 * 三分区：项目记录（列表 + 详情 + 状态动作）/ 经营账本 / 系统设置。
 * 项目详情动作 = 当前 status 的主按钮（引擎状态机的 UI 映射，旧版按钮语义继承）。
 */

import { useEffect, useState } from 'react'
import type { Project } from '../../../gm'
import { COPY } from '../../copy'
import {
  abandonProject,
  accidentDefective,
  accidentRetry,
  publishProject,
  startPlanning,
} from '../../gm-client/actions'
import { useProjectEvents, useRunState, useSave } from '../../gm-client/hooks'
import { pushToast } from '../../gm-client/toast'
import { clearApiKey, hasApiKey, writeApiKey, type KeyMode } from '../../save/apikey'
import { downloadExport, importFromText, restoreLatestBackup } from '../../save/export-import'
import { ensureInitialized } from '../../save/seed'
import { EventFeed } from '../components/EventFeed'
import { SandboxFrame } from '../components/SandboxFrame'
import { DESK, type ComputerTab, type Screen } from '../nav'

const C = COPY.computer

export function ComputerScreen({
  tab,
  projectId,
  go,
}: {
  tab: ComputerTab
  projectId?: string
  go: (s: Screen) => void
}) {
  return (
    <div className="screen deep-screen">
      <button className="btn back-btn" type="button" onClick={() => go(DESK)}>
        {COPY.nav.backToDesk}
      </button>
      <div className="tab-row">
        <button className={tab === 'projects' ? 'tab tab-active' : 'tab'} onClick={() => go({ name: 'computer', tab: 'projects' })}>
          {C.tabProjects}
        </button>
        <button className={tab === 'ledger' ? 'tab tab-active' : 'tab'} onClick={() => go({ name: 'computer', tab: 'ledger' })}>
          {C.tabLedger}
        </button>
        <button className={tab === 'settings' ? 'tab tab-active' : 'tab'} onClick={() => go({ name: 'computer', tab: 'settings' })}>
          {C.tabSettings}
        </button>
      </div>
      {tab === 'projects' && <ProjectsTab projectId={projectId} go={go} />}
      {tab === 'ledger' && <LedgerTab />}
      {tab === 'settings' && <SettingsTab />}
    </div>
  )
}

// ── 项目记录 ────────────────────────────────────────────────────────────────

function ProjectsTab({ projectId, go }: { projectId?: string; go: (s: Screen) => void }) {
  const { bundle } = useSave()
  const projects = [...bundle.projects.projects].sort((a, b) =>
    b.created_at.localeCompare(a.created_at),
  )
  const current = projectId ? projects.find((p) => p.id === projectId) : undefined

  if (current) return <ProjectDetail project={current} go={go} />

  return (
    <div className="panel">
      <div className="btn-row">
        <button className="btn btn-primary" type="button" onClick={() => go({ name: 'newProject' })}>
          {C.newProject}
        </button>
      </div>
      {projects.length === 0 && <p className="dim">{C.emptyProjects}</p>}
      {projects.map((p) => (
        <button
          key={p.id}
          type="button"
          className="list-row"
          onClick={() => go({ name: 'computer', tab: 'projects', projectId: p.id })}
        >
          <span className="list-title">{p.idea.slice(0, 40)}</span>
          <span className="badge">{C.status[p.status]}</span>
        </button>
      ))}
    </div>
  )
}

function ProjectDetail({ project, go }: { project: Project; go: (s: Screen) => void }) {
  const { bundle } = useSave()
  const events = useProjectEvents(project.id)
  const runState = useRunState(project.id)
  const [confirming, setConfirming] = useState<null | 'publish' | 'abandon'>(null)
  const decisions = bundle.decisions.decisions.filter((d) => d.project_id === project.id)
  const review = bundle.reviews.reviews.find((r) => r.project_id === project.id)

  // 事故三选开放判定：developing + 阶段灯 idle + 最近一次 DEV_ACCIDENT 之后没有新的 coding/acceptance 阶段
  const accidentOpen = (() => {
    if (project.status !== 'developing') return false
    if (runState && runState.phase !== 'idle') return false
    let lastAccident = -1
    let lastProgress = -1
    events.forEach((e, i) => {
      if (e.kind === 'error' && e.payload.code === 'DEV_ACCIDENT') lastAccident = i
      if (e.kind === 'phase' && (e.payload.phase === 'coding' || e.payload.phase === 'acceptance'))
        lastProgress = i
    })
    return lastAccident >= 0 && lastAccident > lastProgress
  })()

  return (
    <div className="panel">
      <button className="btn" type="button" onClick={() => go({ name: 'computer', tab: 'projects' })}>
        {COPY.nav.back}
      </button>
      <h3>{project.idea}</h3>
      <p>
        <span className="badge">{C.status[project.status]}</span>{' '}
        <span className="dim mono">{project.id}</span>
      </p>

      {decisions.length > 0 && (
        <p className="dim">
          {C.decisionProgress(
            decisions.filter((d) => d.status === 'resolved').length,
            decisions.length,
          )}
        </p>
      )}

      {/* 状态主按钮（引擎状态机的 UI 映射） */}
      <div className="btn-row">
        {project.status === 'drafting' && (
          <button className="btn btn-primary" type="button" onClick={() => void startPlanning(project.id)}>
            {C.startWork}
          </button>
        )}
        {project.status === 'awaiting_decision' && (
          <button className="btn btn-primary" type="button" onClick={() => go({ name: 'decisions' })}>
            {C.goDecide}
          </button>
        )}
        {project.status === 'in_review' && (
          <button className="btn btn-primary" type="button" onClick={() => setConfirming('publish')}>
            {C.submitPublish}
          </button>
        )}
        {project.status === 'completed' && (
          <button className="btn" type="button" onClick={() => go({ name: 'review', projectId: project.id })}>
            {C.viewReview}
          </button>
        )}
        {project.status !== 'completed' && (
          <button className="btn btn-danger" type="button" onClick={() => setConfirming('abandon')}>
            {C.abandon}
          </button>
        )}
      </div>

      {confirming && (
        <div className="confirm-box">
          <p>{confirming === 'publish' ? C.publishConfirm : C.abandonConfirm}</p>
          <div className="btn-row">
            <button
              className="btn btn-danger"
              type="button"
              onClick={() => {
                setConfirming(null)
                if (confirming === 'publish') void publishProject(project.id)
                else void abandonProject(project.id)
              }}
            >
              {C.confirm}
            </button>
            <button className="btn" type="button" onClick={() => setConfirming(null)}>
              {C.cancel}
            </button>
          </div>
        </div>
      )}

      {/* 事故三选（复用三选一形态，瞬态门禁直接入队） */}
      {accidentOpen && (
        <div className="graybox graybox-warn">
          <h4>{COPY.decisions.accidentTitle}</h4>
          <div className="btn-row">
            <button className="btn" type="button" onClick={() => void accidentRetry(project.id)}>
              {COPY.decisions.accidentRetry}
            </button>
            <button className="btn" type="button" onClick={() => void accidentDefective(project.id)}>
              {COPY.decisions.accidentDefective}
            </button>
            <button className="btn btn-danger" type="button" onClick={() => void abandonProject(project.id)}>
              {COPY.decisions.accidentAbandon}
            </button>
          </div>
        </div>
      )}

      {/* 待验收 / 已上架：试玩沙箱 */}
      {(project.status === 'in_review' || project.status === 'completed') && (
        <SandboxFrame projectId={project.id} title={project.idea} />
      )}

      {/* 开发中：现场播报（只读，极简模式无干预按钮） */}
      {project.status !== 'completed' && <EventFeed events={events} />}
      {review && project.status === 'completed' && (
        <p className="dim">
          {COPY.review.weighted} {review.weighted_total} · {COPY.review.profit}{' '}
          {review.profit >= 0 ? '+' : ''}
          {review.profit}
        </p>
      )}
    </div>
  )
}

// ── 经营账本 ────────────────────────────────────────────────────────────────

function LedgerTab() {
  const { bundle } = useSave()
  const save = bundle.save
  const reviews = bundle.reviews.reviews
  return (
    <div className="panel">
      <table className="ledger-table">
        <tbody>
          <tr><td>{C.ledger.funds}</td><td className="mono">{save.funds}</td></tr>
          <tr><td>{C.ledger.level}</td><td className="mono">Lv.{save.level}</td></tr>
          <tr><td>{C.ledger.exp}</td><td className="mono">{save.exp}</td></tr>
          <tr><td>{C.ledger.tokenTotal}</td><td className="mono">{save.total_token_cost}</td></tr>
          <tr><td>{C.ledger.bailout}</td><td className="mono">{save.bailout.times_used}</td></tr>
          <tr><td>{C.ledger.projectsDone}</td><td className="mono">{save.projects_completed}</td></tr>
        </tbody>
      </table>
      {reviews.length > 0 && (
        <>
          <h4>{COPY.phone.financeDept.reviewTable}</h4>
          <table className="ledger-table">
            <thead>
              <tr><th>{C.ledger.colProject}</th><th>{COPY.review.weighted}</th><th>{COPY.review.revenue}</th><th>{COPY.review.cost}</th><th>{COPY.review.profit}</th></tr>
            </thead>
            <tbody>
              {reviews.map((r) => (
                <tr key={r.id}>
                  <td className="mono">{r.project_id}</td>
                  <td className="mono">{r.weighted_total}</td>
                  <td className="mono">{r.revenue}</td>
                  <td className="mono">{r.cost}</td>
                  <td className="mono">{r.profit >= 0 ? '+' : ''}{r.profit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {bundle.market.break_even_table && (
        <>
          <h4>{C.ledger.breakEven}</h4>
          <table className="ledger-table">
            <tbody>
              {bundle.market.break_even_table.map((row) => (
                <tr key={row.team}>
                  <td>{row.team}</td>
                  <td className="mono">{row.cost}</td>
                  <td className="mono">{row.break_even_score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}

// ── 系统设置（Key / 导入导出 / 回滚 / 重置）────────────────────────────────

function SettingsTab() {
  const [keyInput, setKeyInput] = useState('')
  const [mode, setMode] = useState<KeyMode>('local')
  const [importErrors, setImportErrors] = useState<string[]>([])
  const [confirmReset, setConfirmReset] = useState(false)
  const S = COPY.settings

  // 重置后视图刷新：重置即清空重灌，刷新页面最干净
  useEffect(() => setImportErrors([]), [])

  return (
    <div className="panel">
      <h4>{S.keySection}</h4>
      <p className="dim">{hasApiKey() ? S.keyStatusOn : S.keyStatusOff}</p>
      <input
        className="text-input"
        type="password"
        value={keyInput}
        onChange={(e) => setKeyInput(e.target.value)}
        placeholder="sk-..."
        autoComplete="off"
      />
      <label className="radio-row">
        <input type="radio" checked={mode === 'local'} onChange={() => setMode('local')} />
        {COPY.title.keyModeLocal}
      </label>
      <label className="radio-row">
        <input type="radio" checked={mode === 'session'} onChange={() => setMode('session')} />
        {COPY.title.keyModeSession}
      </label>
      <div className="btn-row">
        <button
          className="btn btn-primary"
          type="button"
          disabled={!keyInput.trim()}
          onClick={() => {
            writeApiKey(keyInput, mode)
            setKeyInput('')
            pushToast(COPY.title.keySaved)
          }}
        >
          {COPY.title.keyEnter}
        </button>
        <button
          className="btn btn-danger"
          type="button"
          onClick={() => {
            clearApiKey()
            pushToast(S.keyClear)
          }}
        >
          {S.keyClear}
        </button>
      </div>

      <h4>{S.saveSection}</h4>
      <div className="btn-row">
        <button
          className="btn"
          type="button"
          onClick={() => {
            void downloadExport().then((name) => pushToast(S.exported(name)))
          }}
        >
          {S.export}
        </button>
        <label className="btn">
          {S.import}
          <input
            type="file"
            accept="application/json"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (!file) return
              void file.text().then(async (text) => {
                const result = await importFromText(text)
                if (result.ok) {
                  setImportErrors([])
                  pushToast(S.importOk)
                } else {
                  setImportErrors(result.errors)
                }
              })
              e.target.value = ''
            }}
          />
        </label>
        <button
          className="btn"
          type="button"
          onClick={() => pushToast(restoreLatestBackup() ? S.restored : S.noBackup)}
        >
          {S.restore}
        </button>
      </div>
      {importErrors.length > 0 && (
        <div className="graybox graybox-warn">
          <p>{S.importFail}</p>
          <ul>
            {importErrors.slice(0, 10).map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      <h4 className="danger-heading">{S.reset}</h4>
      {!confirmReset ? (
        <button className="btn btn-danger" type="button" onClick={() => setConfirmReset(true)}>
          {S.reset}
        </button>
      ) : (
        <div className="confirm-box">
          <p>{S.resetConfirm}</p>
          <div className="btn-row">
            <button
              className="btn btn-danger"
              type="button"
              onClick={() => {
                // 清空外壳 thirdplay:v2:*（除 apikey 两键外）+ 引擎 tp3:*，再灌种子
                const keep = new Set(['thirdplay:v2:apikey', 'thirdplay:v2:apikey-mode'])
                const doomed: string[] = []
                for (let i = 0; i < localStorage.length; i++) {
                  const k = localStorage.key(i)
                  if (!k || keep.has(k)) continue
                  if (k.startsWith('thirdplay:v2:') || k.startsWith('tp3:')) doomed.push(k)
                }
                doomed.forEach((k) => localStorage.removeItem(k))
                ensureInitialized()
                location.reload()
              }}
            >
              {C.confirm}
            </button>
            <button className="btn" type="button" onClick={() => setConfirmReset(false)}>
              {C.cancel}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
